import React, { FC } from 'react';
import useActivities from 'FeatureModules/Activity/hooks/useActivities';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import { EmptyStateCard, CardIllustration, ThemeButton } from 'DesignSystem';
import { Box } from '@mui/material';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { Plus } from '@styled-icons/bootstrap';
import PartnerSkeleton from 'FeatureModules/Partner/Components/Skeleton/PartnerSkeleton';
import ActivitiesTable from 'FeatureModules/Activity/Components/ActivitiesTable/ActivitiesTable';
import { EActivitiesTableColumnKeys } from 'FeatureModules/Activity/Components/ActivitiesTable/activitiesHeaderColumns';
import { useParams } from 'react-router-dom';
import { TRouteParamsWithId } from 'types/router.types';
import { useQuery } from '@apollo/client';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { GET_BASE_PARTNER_COMPANY } from 'graphql/secure/queries/partner.queries';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import ErrorState from 'Components/ErrorState';
import EmptySearchResults from 'Components/EmptySearchResults';
import ActionContainer from 'Components/ActionContainer';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

const PartnerActivities: FC = () => {
  const { id: partnerId } = useParams<TRouteParamsWithId>();

  const { data: partnerData } = useQuery<{ partner: IBasePartnerCompany }>(
    GET_BASE_PARTNER_COMPANY,
    {
      variables: {
        id: partnerId,
      },
      skip: !partnerId,
    }
  );

  const {
    activities,
    loading,
    error,
    searchTerm,
    hasNextPage,
    handlePageEndReached,
    handleSearchTermChange,
    handleCreateActivity,
    handleEditActivity,
    handleSelectActivity,
  } = useActivities({
    defaultOwnedById: partnerId,
    additionalAddEditDialogProps: {
      partner: partnerData?.partner,
    },
  });

  if (error) return <ErrorState />;

  if (!activities.length && !searchTerm && !loading) {
    return (
      <PageContainer>
        <PageTitle
          title="Partner activities"
          goBackLabel="All partners"
          goBackUrl="/partners"
          titleExtension={partnerData?.partner?.name}
        />
        <Box mt={4}>
          <EmptyStateCard
            data-testid="activities-getting-started-card"
            data-testid-button="activities-getting-started-card-button"
            title="Add the partner activities you know of to give your collaborator a head start"
            variant={EItemTypes.LOCATION}
            buttonText="Add new activity"
            background={EBackgroundTypes.WORLD}
            size={EAvailableSizes.LARGE}
            renderIllustration={
              <CardIllustration
                image={EIllustrationTypes.LOCATIONS}
                width={300}
                right={25}
                top={0}
              />
            }
            onCreateItemClick={handleCreateActivity}
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Partner activities"
        goBackLabel="All partners"
        goBackUrl="/partners"
        titleExtension={partnerData?.partner?.name}
      />
      <Box mt={2} position="relative">
        <ActionContainer>
          <SearchToolbar
            setSearchTerm={handleSearchTermChange}
            searchTerm={searchTerm || ''}
            className="search-input"
            data-testid="activity-search-input"
            placeholder="Search activity"
            actions={
              <Box ml={1}>
                <ThemeButton
                  size="medium"
                  color="PRIMARY"
                  onClick={handleCreateActivity}
                  startIcon={<Plus size={20} color="white" />}
                  data-testid="create-activity-btn"
                >
                  Create activity
                </ThemeButton>
              </Box>
            }
          />
        </ActionContainer>

        {loading && (
          <Box position="relative" mt={20}>
            <Loader />
          </Box>
        )}

        {activities.length > 0 && !loading && (
          <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
            <ActivitiesTable
              columns={[
                EActivitiesTableColumnKeys.TITLE,
                EActivitiesTableColumnKeys.MODIFIED_TIMESTAMP,
                EActivitiesTableColumnKeys.ACTIONS,
              ]}
              activities={activities}
              onSelect={handleSelectActivity}
              onEdit={handleEditActivity}
            />
          </InfiniteScrollWrapper>
        )}
      </Box>

      {!activities.length && !loading && (
        <EmptySearchResults
          message="No search results found"
          action={{
            label: 'Add new activity',
            onClick: handleCreateActivity,
          }}
          skeletonComponent={<PartnerSkeleton title={searchTerm || ''} />}
        />
      )}
    </PageContainer>
  );
};

export default PartnerActivities;
